import { Box, Flex, Text } from "@chakra-ui/react";
import type { MonthlyRewardCardProps } from "../MonthlyRewardCard";
import { getTitleName, getTitlePublisher, getTitleDescription, getTitleReadUrl, hasReleaseReadProgress, isTitleClaimed } from "../utils";
import { CoverArea } from "./CoverArea";
import { PriceRow } from "./PriceRow";
import { ClaimButton } from "./ClaimButton";
import { useSlideInMenu } from "../../../components/slideInMenu/hooks/useSlideInMenu";

// Figma: https://www.figma.com/design/z9Ut4lxLJQZK28vEZiHRwN/Monthly-rewards?node-id=118-2811

export function HorizontalRewardCard({ titleData, isClaiming, isGold, onClaim, onSubscribe }: MonthlyRewardCardProps) {
  const displayTitle = getTitleName(titleData);
  const publisher = getTitlePublisher(titleData);
  const description = getTitleDescription(titleData);
  const isClaimed = isTitleClaimed(titleData);
  const readUrl = getTitleReadUrl(titleData);
  const hasReadProgress = hasReleaseReadProgress(titleData);
  const { openReleaseInfoMenu, openBundleDetails } = useSlideInMenu();

  const handleOpenDetails = () => {
    const navigationContext = { area: "gold-rewards" };
    if (titleData.type === "bundle" && titleData.bundle?.uuid) {
      openBundleDetails(titleData.bundle.uuid, navigationContext);
      return;
    }
    if (titleData.type === "release" && titleData.release) {
      const release = titleData.release;
      if (!release.key) return;
      openReleaseInfoMenu(
        release.key,
        release.comic_slug ?? release.comic?.slug,
        navigationContext,
      );
    }
  };

  return (
    <Flex
      gap="32px"
      pl="25px"
      pr="49px"
      py="25px"
      borderRadius="16px"
      border="1px solid"
      borderColor="whiteAlpha.100"
      bg="rgba(255,255,255,0.02)"
      backdropFilter="blur(16px)"
    >
      {/* Cover */}
      <Box w="191px" flexShrink={0}>
        <CoverArea titleData={titleData} onClick={handleOpenDetails} />
      </Box>

      {/* Info + button */}
      <Flex direction="column" gap="24px" flex={1} minW={0} justify="center">
        {/* Title block */}
        <Flex direction="column" gap="16px">
          <Flex direction="column" gap="8px">
            <Text
              fontFamily="Bricolage Grotesque"
              fontSize="24px"
              fontWeight={600}
              color="white"
              lineHeight="24px"
              noOfLines={1}
            >
              {displayTitle}
            </Text>
            {publisher && (
              <Text
                fontFamily="Bricolage Grotesque"
                fontSize="16px"
                fontWeight={600}
                color="neutral.400"
                lineHeight="24px"
                noOfLines={1}
              >
                {publisher}
              </Text>
            )}
          </Flex>
          {description && (
            <Text
              fontSize="16px"
              fontWeight={400}
              color="neutral.300"
              lineHeight="26px"
              noOfLines={3}
            >
              {description}
            </Text>
          )}
        </Flex>

        {/* Price + button */}
        <Flex direction="column" gap="16px">
          <PriceRow retailPrice={titleData.retail_price} />
          <Box w="268px">
            <ClaimButton isGold={isGold} isClaimed={isClaimed} isClaiming={isClaiming} readUrl={readUrl} hasReadProgress={hasReadProgress} onClaim={onClaim} onSubscribe={onSubscribe} />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
}
